// Shared billing constants. Single source of truth for cycle + currency values used by
// both the server (billing service, subscriptions, invoices, etc.) and the web app
// (Select Plan toggle, admin form, payment screens). Adding a new cycle or currency here
// propagates everywhere.

import { DEFAULT_CURRENCY_DECIMALS } from "./decimalClean.js";

// ==================== Billing Cycles ====================

export const BILLING_CYCLE = {
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly",
  ONE_TIME: "one_time",
} as const;

export type BillingCycle = (typeof BILLING_CYCLE)[keyof typeof BILLING_CYCLE];

// All supported cycles as an array — iterate this, never hardcode the list elsewhere.
export const BILLING_CYCLE_VALUES: readonly BillingCycle[] = Object.values(BILLING_CYCLE);

// Singular noun for "per X" pricing labels. Extend when adding a new cycle.
export const BILLING_CYCLE_PERIOD_LABEL: Record<BillingCycle, string> = {
  [BILLING_CYCLE.MONTHLY]: "month",
  [BILLING_CYCLE.QUARTERLY]: "quarter",
  [BILLING_CYCLE.YEARLY]: "year",
  [BILLING_CYCLE.ONE_TIME]: "one-time",
};

// ==================== Currencies ====================

export const CURRENCY = {
  INR: "INR",
  USD: "USD",
  GBP: "GBP",
} as const;

export type Currency = (typeof CURRENCY)[keyof typeof CURRENCY];

// All supported currencies as an array — iterate, never hardcode the list elsewhere.
export const CURRENCY_VALUES: readonly Currency[] = Object.values(CURRENCY);

// ISO 4217 minor-unit digits: the decimals an amount in the currency is rounded to, and the power of ten a payment
// gateway counts it in (`dec.toMinorUnits`). A currency added above needs its digits here (JPY 0, KWD 3).
export const CURRENCY_DECIMALS: Record<Currency, number> = {
  [CURRENCY.INR]: 2,
  [CURRENCY.USD]: 2,
  [CURRENCY.GBP]: 2,
};

// The minor-unit digits of a billing currency code. Stored codes are free-form strings, so a code outside CURRENCY
// takes the platform default (DEFAULT_CURRENCY_DECIMALS), as every billing amount did before currencies had digits.
export const currencyDecimals = (currencyCode: string): number => {
  const currency = CURRENCY_VALUES.find((code) => code === currencyCode);
  return currency === undefined ? DEFAULT_CURRENCY_DECIMALS : CURRENCY_DECIMALS[currency];
};

export const TAN_REGEX = /^[A-Z]{4}[0-9]{5}[A-Z]$/;

export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export const BILLING_FIELD_MAX_LENGTH = {
  userName: 100,
  organization: 100,
  city: 50,
  pincode: 50,
  line1: 100,
  line2: 100,
};

export const TEMP_SUBSCRIPTION_STALE_MS = 15 * 60 * 1000;

// DodoPayments environment. Matches the SDK's own `CheckoutMode` union exactly, so the value the
// server resolves can be handed straight to DodoPayments.Initialize without a cast.
export const DODO_MODE = {
  TEST: "test",
  LIVE: "live",
} as const;

export type DodoMode = (typeof DODO_MODE)[keyof typeof DODO_MODE];

export const DODO_MODE_VALUES = [DODO_MODE.TEST, DODO_MODE.LIVE] as const;

// Display symbol per currency. Used by both billing flows and customer-facing price labels.
export const CURRENCY_SYMBOL: Record<Currency, string> = {
  [CURRENCY.INR]: "₹",
  [CURRENCY.USD]: "$",
  [CURRENCY.GBP]: "£",
};

// Human-readable name. Useful for admin dropdowns and invoice metadata.
export const CURRENCY_NAME: Record<Currency, string> = {
  [CURRENCY.INR]: "Indian Rupee",
  [CURRENCY.USD]: "US Dollar",
  [CURRENCY.GBP]: "British Pound",
};
